"use client";

import { ComponentPropsWithoutRef, forwardRef } from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { Tooltip as TooltipHeroui } from "@heroui/react";
import { Button as ButtonHeroui } from "@heroui/button";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type TooltipIconButtonProps = ComponentPropsWithoutRef<typeof Button> & {
  tooltip: string;
  side?: "top" | "bottom" | "left" | "right";
};

// we still need this component for the arrow to go bottom when we have a big
export const TooltipIconButton = forwardRef<
  HTMLButtonElement,
  TooltipIconButtonProps
>(({ children, tooltip, side = "bottom", className, ...rest }, ref) => {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            {...rest}
            className={cn("size-6 p-1", className)}
            ref={ref}
          >
            {children}
            <span className="sr-only">{tooltip}</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent side={side}>{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
});

TooltipIconButton.displayName = "TooltipIconButton";

export type TooltipIconButtonHerouiProps = Omit<
  ComponentPropsWithoutRef<typeof ButtonHeroui>,
  "children"
> & {
  tooltip: string;
  side?: "top" | "bottom" | "left" | "right";
  children: ((disabled?: boolean) => React.ReactNode) | React.ReactNode;
  isStopButton?: boolean;
};

export const TooltipIconButtonHeroui = forwardRef<
  HTMLButtonElement,
  TooltipIconButtonHerouiProps
>(({ children, tooltip, side = "bottom", className, ...rest }, ref) => {
  const disabled = rest.disabled;
  const color = rest.isStopButton || disabled ? "default" : "primary";
  const variant = rest.isStopButton || disabled ? "flat" : "solid";
  const onClick = rest.onClick;

  return (
    <TooltipHeroui showArrow content={tooltip}>
      <ButtonHeroui
        isIconOnly
        color={color}
        isDisabled={disabled}
        radius="full"
        variant={variant}
        type="submit"
        ref={ref}
        onClick={onClick}
      >
        {typeof children === "function" ? children(disabled) : children}
      </ButtonHeroui>
    </TooltipHeroui>
  );
});

TooltipIconButtonHeroui.displayName = "TooltipIconButtonHeroui";
